import { sectionRenderer } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 8;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

/**
 * A block that renders the shopper's recently viewed products, fetched client-side from
 * whichever product IDs are stored in localStorage by `RecentlyViewed`.
 */
class RecentlyViewedProductsBlock extends HTMLElement {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  async #loadProducts() {
    const { sectionId } = this.dataset;
    const id = this.id;

    if (!sectionId || !id) return;

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.#hide();
      return;
    }

    const url = new URL(this.dataset.url ?? '', window.location.origin);
    url.searchParams.set('q', viewedProducts.map((productId) => `id:${productId}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const newElement = parsedHTML.getElementById(id);

      if (newElement?.innerHTML.trim()) {
        this.innerHTML = newElement.innerHTML;
      } else {
        this.#hide();
      }
    } catch (error) {
      this.#handleError(error);
    }
  }

  /**
   * @param {unknown} error
   */
  #handleError(error) {
    console.error('Recently viewed products error:', error);
    this.#hide();
  }

  #hide() {
    this.classList.add('hidden');
    this.dataset.error = 'No recently viewed products';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProductsBlock);
}
